import { fetchLADSBinary } from "../api/lads";

/**
 * Resolves a device's DeviceTypeImage links to displayable object URLs.
 *
 * The `lads/binary/{nodeId}` endpoint is oauth2-protected, so the image must be
 * fetched through the authenticated client (a plain <img src> can't send the
 * Bearer token). Results are cached by nodeId for the session to avoid
 * refetching the same image across components/renders.
 */
const cache = new Map<string, Promise<string>>();

/** Extract the raw OPC UA nodeId from a `lads/binary/{encodedNodeId}` link. */
export function extractImageNodeId(link: string): string | undefined {
  const marker = "/binary/";
  const idx = link.indexOf(marker);
  if (idx < 0) return undefined;
  const encoded = link.slice(idx + marker.length);
  return encoded ? decodeURIComponent(encoded) : undefined;
}

/**
 * Resolve a deviceTypeImage link to an object URL. Returns undefined for an
 * unparseable link; rejects if the fetch fails (caller falls back to the
 * default image).
 */
export function loadDeviceImageUrl(
  link: string,
  accessToken?: string
): Promise<string> | undefined {
  const nodeId = extractImageNodeId(link);
  if (!nodeId) return undefined;

  const cached = cache.get(nodeId);
  if (cached) return cached;

  const promise = fetchLADSBinary(nodeId, accessToken)
    .then((blob) => URL.createObjectURL(blob))
    .catch((error) => {
      cache.delete(nodeId); // don't cache failures — allow a later retry
      throw error;
    });
  cache.set(nodeId, promise);
  return promise;
}
